import type { ReactNode } from 'react'
import { type BillingPeriod, concierge, type Plan, planById } from '../checkout/catalogue'
import {
  annualEffectiveCents,
  annualSavingsPercent,
  firstChargeCents,
  firstKitPercent,
  formatCents,
} from '../checkout/pricing'
import { activeCode, usePromo } from '../promo/store'

/**
 * A plan's prices on the pricing page: one card's block, from the promo's tag
 * down to the last line above the card's divider. The rest of the card is
 * Webflow's.
 *
 * Every number comes from the checkout's catalogue, through the same
 * derivations the checkout shows, so the pricing page and the checkout can't
 * disagree. The plan is priced as it's sold to the visitor: once their promo
 * is active, Kit + Studio's prices are its products', and the block changes
 * the moment they sign up, since it reads the same promo store as the bar.
 *
 * The page's billing toggle is Webflow's, so each card has one embed for each
 * side of it, `data-billing` saying which, and the toggle shows one.
 */

export interface PlanPricingProps {
  /** The card's plan (`data-plan`): `kit-studio`, `online-only` or `concierge`. */
  plan?: unknown
  /** The side of the billing toggle the embed sits on (`data-billing`): `annual` or `monthly`. */
  billing?: unknown
}

function toBilling(value: unknown): BillingPeriod | undefined {
  return value === 'annual' || value === 'monthly' ? value : undefined
}

/** The plan as it's sold to a visitor with `promo` active, or Concierge, which isn't sold here. */
function findPlan(id: unknown, promo: Parameters<typeof planById>[1]): Plan | undefined {
  if (id === concierge.id) return concierge
  return typeof id === 'string' ? planById(id, promo) : undefined
}

export function PlanPricing({ plan: planId, billing: billingValue }: PlanPricingProps) {
  const promo = usePromo(activeCode)
  const plan = findPlan(planId, promo)
  const billing = toBilling(billingValue)

  if (!plan || !billing) {
    console.warn(
      `[codeverse] PlanPricing needs data-plan and data-billing; got ${JSON.stringify(planId)} and ${JSON.stringify(billingValue)}`,
    )
    return null
  }

  // The most popular plan is in the brand's purple, the others in navy.
  const accent = plan.mostPopular ? 'text-price-brand' : 'text-price-ink'
  // Concierge's card names its prices and no saving, as the design has it.
  const showsSaving = plan.id !== concierge.id
  const price = plan[billing]
  const discounted = price.firstKitDiscountCents !== undefined

  return (
    // Polite, so the new prices are announced when a sign-up activates the promo.
    <div
      aria-live="polite"
      className="flex flex-col items-center gap-1 text-center"
      data-testid="plan-pricing"
    >
      {discounted && (
        <PromoTag
          percent={firstKitPercent(price, billing)}
          detail={
            billing === 'monthly'
              ? `You save ${formatCents(price.firstKitDiscountCents ?? 0)}`
              : 'Applied at checkout'
          }
        />
      )}

      {(billing === 'annual' ? showsSaving : discounted) && (
        // Faded, with the price and what it's per each struck through in its own colour.
        <p className={`text-2xl opacity-75 ${accent}`} data-testid="price-was">
          <span className="sr-only">Was </span>
          <span className="line-through">{formatCents(plan.monthly.priceCents)}</span>
          <Per struck>/month</Per>
        </p>
      )}

      {billing === 'monthly' && discounted ? (
        <>
          <Headline
            accent={accent}
            per={firstChargeCents(price) === 0 ? 'First Kit' : 'first month'}
          >
            {firstChargeCents(price) === 0 ? 'FREE' : formatCents(firstChargeCents(price))}
          </Headline>
          <p className={`text-[0.9rem] leading-normal ${accent}`} data-testid="price-then">
            then {formatCents(price.priceCents)}/month
          </p>
        </>
      ) : (
        <Headline accent={accent} per="/month">
          {formatCents(billing === 'annual' ? annualEffectiveCents(plan) : price.priceCents)}
        </Headline>
      )}

      {billing === 'annual' && (
        <div className={`flex flex-col text-[0.9rem] leading-normal ${accent}`}>
          {showsSaving && (
            <p className="font-semibold" data-testid="price-saving">
              <span aria-hidden="true">🎉 </span>
              Saving {annualSavingsPercent(plan)}% with annual billing
            </p>
          )}
          <p data-testid="price-billed">{formatCents(price.priceCents)} billed annually</p>
        </div>
      )}
    </div>
  )
}

/** The promo's green tag above the prices: "✓ First Kit FREE · Applied at checkout". */
function PromoTag({ percent, detail }: { percent: number; detail: string }) {
  return (
    <p
      className="mb-1 max-w-full rounded-full border border-promo-tag-edge bg-promo-green px-4 py-1 text-[13px] leading-snug font-bold tracking-wide text-promo-tag-ink uppercase"
      data-testid="price-promo"
    >
      {/* Inline, so the check stays beside the words when the tag wraps on a phone. */}
      <svg
        width={14}
        height={14}
        viewBox="0 0 14 14"
        aria-hidden="true"
        className="mr-1 inline align-[-2px]"
      >
        <path
          d="m2.5 7.5 3 3 6-7"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <span>
        {percent === 100 ? 'First Kit FREE' : `${percent}% off first month`} · {detail}
      </span>
    </p>
  )
}

/** The big number, with what it's per beside it. */
function Headline({ accent, per, children }: { accent: string; per: string; children: ReactNode }) {
  return (
    <p className={`text-[32px] leading-snug font-bold ${accent}`} data-testid="price-headline">
      {children}
      <Per>{per}</Per>
    </p>
  )
}

/**
 * What a price is per, after a space, so "$25.95 /month" reads as two words.
 * Struck, it has a line of its own, in its grey, apart from the price's.
 */
function Per({ struck = false, children }: { struck?: boolean; children: ReactNode }) {
  return (
    <>
      {' '}
      <span className={`text-base font-normal text-price-muted ${struck ? 'line-through' : ''}`}>
        {children}
      </span>
    </>
  )
}
